import { useEffect, useState, type KeyboardEvent } from 'react';
import { searchChatSources } from '@/lib/chat-sources';
import type { ChatSource, ChatSourceOption } from '@shared/types/chat-sources';
import { chatSourceKey } from '@shared/types/chat-sources';

/**
 * D1 — « @ » in the composer opens the source picker: what follows the « @ »
 * filters it, ↑ ↓ choose, Entrée cites, Échap closes. Citing removes the
 * « @… » typed and adds a chip.
 */

/** Wait after a key before searching (one request per pause, not per key). */
const SEARCH_DELAY_MS = 150;

const mentionAt = (value: string, caret: number): { start: number; query: string } | null => {
  const before = value.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start === -1 || (start > 0 && !/\s/.test(before[start - 1] ?? ''))) return null;
  const query = before.slice(start + 1);
  return /\n/.test(query) ? null : { start, query };
};

export const useSourcePicker = (params: {
  value: string;
  setValue: (value: string) => void;
  sources: ChatSource[];
  setSources: (sources: ChatSource[]) => void;
}) => {
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [options, setOptions] = useState<ChatSourceOption[]>([]);
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);
  /** The options answer the current query (none shown as « Aucune source » before that). */
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!mention) return;
    setSearched(false);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      searchChatSources(mention.query.trim())
        .then((found) => {
          if (cancelled) return;
          const cited = new Set(params.sources.map((source) => chatSourceKey(source.ref)));
          setOptions(found.filter((option) => !cited.has(chatSourceKey(option.ref))));
          setActive(0);
          setFailed(null);
          setSearched(true);
        })
        .catch((error: unknown) => {
          if (!cancelled) setFailed(error instanceof Error ? error.message : 'Les sources ne sont pas accessibles.');
        });
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [mention?.query, mention !== null]);

  const onChange = (value: string, caret: number) => {
    params.setValue(value);
    setMention(mentionAt(value, caret));
  };

  const close = () => {
    setMention(null);
    setOptions([]);
  };

  const pick = (option: ChatSourceOption) => {
    if (mention) {
      const end = mention.start + 1 + mention.query.length;
      params.setValue(`${params.value.slice(0, mention.start)}${params.value.slice(end)}`.replace(/^\s+/, ''));
    }
    params.setSources([...params.sources, { ref: option.ref, label: option.label }]);
    close();
  };

  /** True when the key was the picker's (the composer must not send). */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!mention) return false;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return true;
    }
    // Enter while the search has not answered yet would send the « @… » as text.
    if (!searched && event.key === 'Enter') {
      event.preventDefault();
      return true;
    }
    if (!options.length) return false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      return true;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const option = options[active];
      if (!option) return false;
      event.preventDefault();
      pick(option);
      return true;
    }
    return false;
  };

  return { open: mention !== null, searched, options, active, failed, onChange, onKeyDown, pick, close };
};
